// End-of-match overlay: result banner, team scores, MVP, final scoreboard, the rewards block (XP breakdown,
// level, unlocks, weapon levels, rating) and the next-match countdown.

import { div, el, setClass, setText, span } from "./dom";
import { formatTimer, teamName, weaponLabel } from "./format";
import { ScoreLayout } from "./scoretable";
import type { MatchEndView, RewardsView } from "./types";

const fmt = (n: number): string => Math.round(n).toLocaleString("en-US");

/** A "label ... value" row of the rewards block. */
function line(cls: string, label: string, value: string): HTMLDivElement {
  const row = div(`hud-rw__line ${cls}`);
  row.append(span("hud-rw__k", label), span("hud-rw__v", value));
  return row;
}

/**
 * The rewards block: level and XP bar on the left, the itemised XP in the middle, unlocks / weapon levels /
 * rating on the right. Built once per `RewardsView` (it arrives a few ms after the end view).
 */
function buildRewards(r: RewardsView): HTMLElement[] {
  const level = div("hud-rw__col hud-rw__level");
  const head = div("hud-rw__lvhead");
  head.append(span("hud-rw__label", "LEVEL"), span("hud-rw__lv", String(r.levelBefore)));
  if (r.leveledUp) head.append(span("hud-rw__to"), span("hud-rw__lv is-new", String(r.levelAfter)), span("hud-rw__up", "LEVEL UP"));
  const bar = div("hud-rw__bar");
  const fill = span("hud-rw__fill");
  fill.style.transform = `scaleX(${Math.min(1, Math.max(0, r.levelProgress))})`;
  bar.append(fill);
  const xpLine = div("hud-rw__xpline");
  // The LCD face has no "+" and its comma is a speck, so the sign is a separate span and the total has no separator.
  xpLine.append(
    span("hud-rw__plus", "+"),
    span("hud-rw__total", String(Math.round(r.xpTotal))),
    span("hud-rw__unit", "XP"),
    span("hud-rw__into", r.xpForNext > 0 ? `${fmt(r.xpIntoLevel)} / ${fmt(r.xpForNext)}` : "MAX LEVEL"),
  );
  level.append(head, bar, xpLine);

  const items = div("hud-rw__col hud-rw__items");
  items.append(span("hud-rw__label", "XP"));
  const list = el("ul", "hud-rw__list");
  for (const it of r.xpItems) {
    const li = el("li", "hud-rw__item");
    li.append(span("hud-rw__k", it.label), span("hud-rw__v", `+${fmt(it.xp)}`));
    list.append(li);
  }
  items.append(list);

  const side = div("hud-rw__col hud-rw__side");
  if (r.ratingDelta !== null) {
    const d = r.ratingDelta;
    // Sign and arrow carry the direction; the colour only backs it up.
    const text = d === 0 ? "\u00b10" : d > 0 ? `\u25b2 +${fmt(d)}` : `\u25bc \u2212${fmt(-d)}`;
    const row = line("hud-rw__rating", "RATING", `${fmt(r.rating)}  ${text}`);
    row.dataset.dir = d > 0 ? "up" : d < 0 ? "down" : "flat";
    side.append(row);
  }
  for (const w of r.weaponLevels) {
    const row = line(
      w.leveledUp ? "hud-rw__wpn is-up" : "hud-rw__wpn",
      weaponLabel(w.weapon),
      w.leveledUp ? `LVL ${w.levelBefore} \u203a ${w.levelAfter}` : `LVL ${w.levelAfter}  +${fmt(w.xpGained)}`,
    );
    side.append(row);
  }
  if (r.unlocked.length > 0) {
    const chips = div("hud-rw__unlocks");
    chips.append(span("hud-rw__label", "UNLOCKED"));
    for (const u of r.unlocked) chips.append(span("hud-rw__chip", u));
    side.append(chips);
  }
  return [level, items, side];
}

export class MatchEnd {
  readonly root = div("hud-end");
  private readonly titleEl = div("hud-end__title");
  private readonly subEl = div("hud-end__sub");
  private readonly scoreEl = div("hud-end__score");
  private readonly scoreL = span("hud-end__team");
  private readonly scoreR = span("hud-end__team");
  private readonly nameL = span("hud-end__tname");
  private readonly numL = span("hud-end__tnum");
  private readonly nameR = span("hud-end__tname");
  private readonly numR = span("hud-end__tnum");
  private readonly mvpEl = div("hud-end__mvp");
  private readonly nextEl = div("hud-end__next");
  private readonly nextLabel = span("hud-end__nlabel", "NEXT MATCH IN");
  private readonly lobbyEl = div("hud-end__lobby");
  private readonly nextNum = span("hud-end__nnum");
  private readonly layout = new ScoreLayout();
  private readonly rewardsEl = div("hud-rw");
  private rewardsShown: RewardsView | null = null;
  private active = false;
  private nextBase: number | null = null;
  private setAtMs = 0;
  private nextShown = -2;

  constructor() {
    const zoom = div("hud-zoom");
    const panel = div("hud-end__panel");
    this.scoreL.append(this.nameL, this.numL);
    this.scoreR.append(this.numR, this.nameR);
    this.scoreEl.append(this.scoreL, span("hud-end__dash"), this.scoreR);
    this.nextEl.append(this.nextLabel, this.nextNum);
    this.rewardsEl.setAttribute("role", "group");
    this.rewardsEl.setAttribute("aria-label", "Match rewards");
    this.rewardsEl.hidden = true;
    this.lobbyEl.hidden = true;
    panel.append(this.titleEl, this.subEl, this.scoreEl, this.mvpEl, this.layout.root, this.rewardsEl, this.nextEl, this.lobbyEl);
    zoom.append(panel);
    this.root.append(zoom);
    this.root.hidden = true;
  }

  get isActive(): boolean {
    return this.active;
  }

  set(view: MatchEndView | null, nowMs: number): void {
    this.active = view !== null;
    this.root.hidden = view === null;
    if (!view) {
      this.nextBase = null;
      this.setRewards(null);
      return;
    }
    this.root.dataset.tone = view.tone;
    setText(this.titleEl, view.title);
    setText(this.subEl, view.subtitle);
    const teams = view.teamScores !== null;
    const left = this.layout.render(view.rows, teams, view.teamScores, false);
    setClass(this.scoreEl, "is-hidden", !teams);
    if (view.teamScores) {
      const ls = left === "a" ? view.teamScores[0] : view.teamScores[1];
      const rs = left === "a" ? view.teamScores[1] : view.teamScores[0];
      const lt = left;
      const rt = left === "a" ? "b" : "a";
      this.scoreL.className = `hud-end__team hud-team-${lt}`;
      this.scoreR.className = `hud-end__team hud-team-${rt}`;
      setText(this.nameL, teamName(lt));
      setText(this.numL, String(ls));
      setText(this.nameR, teamName(rt));
      setText(this.numR, String(rs));
    }
    setText(this.mvpEl, view.mvpName ? `MVP  ${view.mvpName}` : "");
    this.mvpEl.hidden = !view.mvpName;
    this.setRewards(view.rewards);
    // A custom lobby does not rotate to the next match by itself: it returns to the lobby, where the host starts it.
    setText(this.nextLabel, view.lobby === undefined ? "NEXT MATCH IN" : "BACK IN THE LOBBY IN");
    setText(this.lobbyEl, view.lobby ?? "");
    this.lobbyEl.hidden = view.lobby === undefined;
    this.nextBase = view.nextInSec;
    this.setAtMs = nowMs;
    this.nextShown = -2;
    this.updateNext(nowMs);
  }

  /** `set()` runs again when the rewards arrive; the block is rebuilt only when the rewards object changed. */
  private setRewards(r: RewardsView | null): void {
    if (r === this.rewardsShown) return;
    this.rewardsShown = r;
    this.rewardsEl.hidden = r === null;
    setClass(this.rewardsEl, "is-levelup", r !== null && r.leveledUp);
    this.rewardsEl.replaceChildren(...(r === null ? [] : buildRewards(r)));
  }

  /** Per frame: tick the "next match" countdown (text changes once a second). */
  update(nowMs: number): void {
    if (this.active && this.nextBase !== null) this.updateNext(nowMs);
  }

  private updateNext(nowMs: number): void {
    const base = this.nextBase;
    if (base === null) {
      this.nextEl.hidden = true;
      return;
    }
    const left = Math.max(0, Math.ceil(base - (nowMs - this.setAtMs) / 1000));
    if (left === this.nextShown) return;
    this.nextShown = left;
    this.nextEl.hidden = false;
    setText(this.nextNum, formatTimer(left));
  }
}
